import { useCallback, useMemo, useState } from "react";

/** A set of selected item ids and the operations on it. Every operation is a stable callback. */
export interface Selection<Id extends string = string> {
  selectedIds: ReadonlySet<Id>;
  count: number;
  isSelected: (id: Id) => boolean;
  /** Whether every id in `ids` is selected; false for an empty list. */
  areAllSelected: (ids: readonly Id[]) => boolean;
  select: (id: Id) => void;
  unselect: (id: Id) => void;
  toggle: (id: Id) => void;
  /** Adds every id in `ids`, keeping what was already selected. */
  selectAll: (ids: readonly Id[]) => void;
  unselectAll: () => void;
  /** Replaces the selection with exactly `ids`. */
  selectOnly: (ids: readonly Id[]) => void;
  /** Unselects `ids` when all of them are selected, otherwise selects all of them. */
  toggleAll: (ids: readonly Id[]) => void;
  /** Adds the ids between `fromId` and `toId` (inclusive, either order) in `orderedIds` — a
   *  shift-click range. Does nothing if either end isn't in `orderedIds`. */
  selectRange: (orderedIds: readonly Id[], fromId: Id, toId: Id) => void;
}

/** Tracks which items are selected, by id. */
export function useSelection<Id extends string = string>(): Selection<Id> {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<Id>>(() => new Set());

  const select = useCallback((id: Id) => {
    setSelectedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const unselect = useCallback((id: Id) => {
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const toggle = useCallback((id: Id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: readonly Id[]) => {
    setSelectedIds((prev) => new Set([...prev, ...ids]));
  }, []);

  const unselectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const selectOnly = useCallback((ids: readonly Id[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const toggleAll = useCallback((ids: readonly Id[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const selectRange = useCallback((orderedIds: readonly Id[], fromId: Id, toId: Id) => {
    const from = orderedIds.indexOf(fromId);
    const to = orderedIds.indexOf(toId);
    if (from === -1 || to === -1) return;
    const range = orderedIds.slice(Math.min(from, to), Math.max(from, to) + 1);
    setSelectedIds((prev) => new Set([...prev, ...range]));
  }, []);

  const isSelected = useCallback((id: Id) => selectedIds.has(id), [selectedIds]);

  const areAllSelected = useCallback(
    (ids: readonly Id[]) => ids.length > 0 && ids.every((id) => selectedIds.has(id)),
    [selectedIds]
  );

  return useMemo(
    () => ({
      selectedIds,
      count: selectedIds.size,
      isSelected,
      areAllSelected,
      select,
      unselect,
      toggle,
      selectAll,
      unselectAll,
      selectOnly,
      toggleAll,
      selectRange,
    }),
    [
      selectedIds,
      isSelected,
      areAllSelected,
      select,
      unselect,
      toggle,
      selectAll,
      unselectAll,
      selectOnly,
      toggleAll,
      selectRange,
    ]
  );
}
